import { Component } from "@theme/component";
import { formatCurrency, fetchConfig } from "@theme/utilities";
import { CartUpdateEvent, CartGroupedSections, ThemeEvents } from "@theme/events";
import { markExperienceUsed, prepareExperienceAdd, quizRecommendationUrl, trackExperience } from "@theme/experience-tracking";

const STEP_ORDER = ["intro", "q1", "q2", "q3", "email", "results"];

/** Keep curated rankings, alternating types for the mixed wear choice. */
const quizCandidateHandles = (combos, energy, intent, wear, wearTypes = null) => {
  const types = wear === "all"
    ? (wearTypes || ["necklace", "charms", "rings-toe-rings", "bracelets-anklets"])
    : [wear];
  const matches = types.map((type) => combos[`${energy}|${intent}|${type}`]).filter(Boolean);
  const handles = new Set();
  for (const field of ["star_handles", "other_handles"]) {
    const longest = Math.max(0, ...matches.map((combo) => combo[field].length));
    for (let index = 0; index < longest; index++) {
      for (const combo of matches) {
        if (combo[field][index]) handles.add(combo[field][index]);
      }
    }
  }
  return [...handles];
};

/**
 * Pinned deliberately. Klaviyo versions its API by date and will keep serving
 * this shape; letting it float means a future revision could change the
 * payload contract under us without any deploy on our side.
 */
const KLAVIYO_REVISION = "2026-07-15";
const KLAVIYO_CLIENT_URL = "https://a.klaviyo.com/client";

/**
 * Breathing room, in px, left between the sticky header and the top of the
 * card each time the step changes — without it the card butts straight up
 * against the nav.
 */
const SCROLL_GAP = 16;

/**
 * The store's active ISO currency code.
 *
 * Worth sending alongside the formatted price: this store trades in AUD but
 * formats it as a bare "$", which is unambiguous on the storefront and much
 * less so in an inbox.
 */
const currencyCode = () => window.Shopify?.currency?.active ?? "";

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * Escapes text interpolated into result-card markup. Product titles are
 * merchant-authored and routinely contain `&`, `"` and `|`, which would
 * otherwise break the surrounding HTML.
 *
 * @param {unknown} value
 */
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

/** @param {unknown} value */
const escapeAttribute = (value) => escapeHtml(value);

/**
 * Undoes the HTML escaping Liquid applies on the way out.
 *
 * The blurb is a rich_text metafield, and the only Liquid form that renders it
 * as text rather than as its raw node tree is `metafield_tag | strip_html` —
 * which strips the tags but leaves the entities, so an apostrophe arrives here
 * as `&#39;`. Escaping that again for insertion produces `&amp;#39;`, which the
 * browser renders as a literal `&#39;` on the card.
 *
 * DOMParser rather than a detached element: it decodes every entity, and it
 * never executes anything in the string it parses.
 *
 * @param {unknown} value
 */
const decodeHtmlEntities = (value) => {
  const text = String(value ?? "");
  if (!text.includes("&")) return text;
  return new DOMParser().parseFromString(text, "text/html").documentElement.textContent ?? "";
};

class QuizComponent extends Component {
  answers = { energy: null, intent: null, wear: null };

  /** Set only when the visitor submits the email step; "Skip" leaves it null. */
  #email = null;

  /** Shared in-flight ranking request; reset after failures so retry is possible. */
  static #quizData = null;

  #preparedResults = null;
  #renderVersion = 0;
  #completionTracked = false;

  #config = undefined;

  get #configuration() {
    if (this.#config !== undefined) return this.#config;
    this.#config = null;
    const source = this.querySelector('.quiz__configuration');
    if (source) {
      try {
        const config = JSON.parse(source.textContent);
        if (config.schemaVersion === 1 && config.questions?.length === 3 && config.combos && config.one_liners) this.#config = config;
      } catch (error) { console.warn('[quiz] invalid published configuration', error); }
    }
    return this.#config;
  }

  #wording(key, fallback) {
    const value = this.#configuration?.copy?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resolveFromUrl();
    window.addEventListener("popstate", () => this.#resolveFromUrl());
  }

  start() {
    this.#completionTracked = false;
    void trackExperience("quiz_started");
    // Fetch the small ranking file while the visitor answers the questions.
    this.#loadQuizData().catch(() => {});
    this.#goToStep("q1");
  }

  /**
   * Moves to a step and returns the card to the top of the screen.
   *
   * Every screen after the intro is a full viewport tall, so without this the
   * next question opens already scrolled past its own heading — the reader
   * lands mid-card wherever the previous one left them.
   *
   * @param {string} step
   */
  #goToStep(step) {
    this.dataset.step = step;
    this.#scrollToTop();
  }

  #scrollToTop() {
    // Measured from the card rather than the section: the section carries the
    // decorative padding around the card, so aligning that put the card itself
    // a long way down the screen.
    const card = this.querySelector(".quiz__inner") ?? this;
    // The header is sticky, so stop short of it by its own height or the top
    // of the card slides underneath. The theme publishes that live.
    const header =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    const top = window.scrollY + card.getBoundingClientRect().top - header - SCROLL_GAP;

    // Both directions. This used to bail out unless the page needed scrolling
    // up, which left the card wherever the previous step happened to end —
    // every step should open in the same place on screen.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduced ? "auto" : "smooth" });
  }

  /** @param {Event} event */
  choose(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest(".quiz__option") : null;
    if (!button) return;

    const question = button.dataset.question;
    const value = button.dataset.value;
    if (!question || !value) return;

    this.answers[question] = value;

    const currentIndex = STEP_ORDER.indexOf(this.dataset.step ?? "intro");
    const nextStep = STEP_ORDER[currentIndex + 1];
    if (nextStep) this.#goToStep(nextStep);
    if (nextStep === "email") this.#prepareResults().catch(() => {});
  }

  back() {
    const currentIndex = STEP_ORDER.indexOf(this.dataset.step ?? "intro");
    const previousStep = STEP_ORDER[Math.max(0, currentIndex - 1)];
    this.#goToStep(previousStep);
  }

  skipEmail() {
    this.#recordCompletion();
    this.#goToStep("results");
    this.renderResults();
    this.#pushResultsUrl();
  }

  #recordCompletion() {
    if (this.#completionTracked || !Object.values(this.answers).every(Boolean)) return;
    this.#completionTracked = true;
    void markExperienceUsed("quiz");
    void trackExperience("quiz_completed");
  }

  // ---- Klaviyo ------------------------------------------------------------

  /** Integration is off unless both identifiers are configured. */
  get #klaviyo() {
    const key = this.dataset.klaviyoKey?.trim();
    const list = this.dataset.klaviyoList?.trim();
    return key ? { key, list, metric: this.dataset.klaviyoMetric?.trim() || "Completed Quiz" } : null;
  }

  /**
   * @param {string} path
   * @param {object} body
   */
  async #klaviyoPost(path, body) {
    const config = this.#klaviyo;
    if (!config) return;
    try {
      const response = await fetch(`${KLAVIYO_CLIENT_URL}/${path}/?company_id=${encodeURIComponent(config.key)}`, {
        method: "POST",
        headers: { "content-type": "application/json", revision: KLAVIYO_REVISION },
        body: JSON.stringify(body),
      });
      // Klaviyo answers 202 with an empty body on success; anything else is
      // worth surfacing in the console rather than failing silently, since the
      // visitor's journey carries on regardless.
      if (!response.ok) {
        console.error(`[quiz] Klaviyo ${path} responded ${response.status}`, await response.text().catch(() => ""));
      }
    } catch (error) {
      console.error(`[quiz] Klaviyo ${path} failed`, error);
    }
  }

  /**
   * The human label for an answer, read back out of the option the visitor
   * pressed. Keeps "Protection and Cleansing" in one place — the markup —
   * rather than duplicating the wording in a lookup table here.
   *
   * @param {string} question
   * @param {string | null} value
   */
  #answerLabel(question, value) {
    if (!value) return "";
    const option = this.querySelector(`.quiz__option[data-question="${question}"][data-value="${value}"]`);
    const label = option?.querySelector(".quiz__option-label");
    return label ? label.textContent.replace(/\s+/g, " ").trim() : value;
  }

  #answerProperties() {
    const { energy, intent, wear } = this.answers;
    return {
      Energy: this.#answerLabel("energy", energy),
      Intent: this.#answerLabel("intent", intent),
      Wear: this.#answerLabel("wear", wear),
    };
  }

  /**
   * Consent record and list membership, sent the moment the visitor submits.
   * Deliberately not awaited by the caller: the results are already on screen
   * and this must not hold them up.
   *
   * @param {string} email
   */
  #subscribe(email) {
    const config = this.#klaviyo;
    if (!config?.list) return;

    const answers = this.#answerProperties();
    return this.#klaviyoPost("subscriptions", {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Find Your Piece quiz",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
                properties: {
                  "Quiz Energy": answers.Energy,
                  "Quiz Intent": answers.Intent,
                  "Quiz Wear": answers.Wear,
                  "Quiz Completed At": new Date().toISOString(),
                },
              },
            },
          },
        },
        relationships: { list: { data: { type: "list", id: config.list } } },
      },
    });
  }

  /**
   * Forces an absolute https URL, for values that have to survive outside a
   * browser context.
   *
   * @param {string} value
   */
  #absoluteUrl(value) {
    const url = String(value ?? "").trim();
    if (!url) return "";
    if (url.startsWith("//")) return `https:${url}`;
    try {
      return new URL(url, window.location.origin).href;
    } catch {
      return url;
    }
  }

  /**
   * The event the Klaviyo flow triggers on. Carries the whole match list, not
   * just the three featured picks, so the email can loop over them.
   *
   * Sent after the products resolve rather than on submit, because the
   * recommendations are not known until they have been fetched.
   *
   * @param {string} email
   * @param {any[]} products
   */
  #trackCompletion(email, products) {
    const config = this.#klaviyo;
    if (!config) return;

    const recommendations = products.map((product) => ({
      Title: product.title,
      Handle: product.handle,
      URL: new URL(product.url, window.location.origin).href,
      // Shopify hands back protocol-relative CDN URLs (`//cdn.shopify...`).
      // A browser resolves those against the page; an email client has no page
      // to resolve against, so the image silently fails to load in the flow.
      ImageURL: this.#absoluteUrl(product.featured_image),
      // Three forms, because each is the wrong answer for the other's job.
      // `Price` stays a number so Klaviyo can segment and compare on it — as
      // JSON it cannot hold trailing zeros, so 74.50 arrives as 74.5.
      // `PriceFormatted` is what the email should actually print, built with
      // the theme's own formatter so it matches the storefront exactly.
      Price: Math.round(product.price) / 100,
      PriceFormatted: formatCurrency(product.price),
      Currency: currencyCode(),
    }));

    return this.#klaviyoPost("events", {
      data: {
        type: "event",
        attributes: {
          metric: { data: { type: "metric", attributes: { name: config.metric } } },
          profile: { data: { type: "profile", attributes: { email } } },
          properties: {
            ...this.#answerProperties(),
            MatchCount: recommendations.length,
            Currency: currencyCode(),
            ResultsURL: window.location.href,
            Recommendations: recommendations,
          },
        },
      },
    });
  }

  /** @param {Event} event */
  async submitEmail(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest("button") : null;
    const form = button?.closest("form");
    if (!(form instanceof HTMLFormElement)) return;

    if (!form.reportValidity()) {
      event.preventDefault();
      return;
    }

    // Deliberately do NOT preventDefault: this store has hCaptcha spam
    // protection on the native customer form, which needs a real submit
    // event to inject its token (same as this theme's own
    // snippets/newsletter-form.liquid — a fetch/preventDefault bypass gets
    // a silent 400 "Missing CAPTCHA token"). The form's target="quiz-email-target"
    // attribute (set in sections/quiz.liquid) routes the real submission
    // into a hidden iframe instead of navigating the page, so the quiz's
    // in-memory state and instant "advance to results" UX are unaffected.
    // Read the address before the native submit clears the field.
    const input = form.querySelector('input[type="email"]');
    this.#email = input instanceof HTMLInputElement ? input.value.trim() : null;

    this.#recordCompletion();
    this.#goToStep("results");
    // Push the URL before the event is sent: `ResultsURL` is read off
    // window.location, and it has to carry the answers to be worth anything.
    this.#pushResultsUrl();

    // Consent and list membership go first, unawaited, so they are recorded
    // even if the product fetches below are slow or fail outright.
    if (this.#email) this.#subscribe(this.#email);

    const products = await this.renderResults();
    if (this.#email && products.length) this.#trackCompletion(this.#email, products);
  }

  #pushResultsUrl() {
    const { energy, intent, wear } = this.answers;
    const params = new URLSearchParams({ energy, intent, wear });
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ quizStep: "results" }, "", url);
  }

  #resolveFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const energy = params.get("energy");
    const intent = params.get("intent");
    const wear = params.get("wear");

    if (![energy, intent, wear].every(value => value && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value))) {
      this.dataset.step = "intro";
      return;
    }

    this.answers = { energy, intent, wear };
    this.dataset.step = "results";
    this.renderResults();
  }

  async #loadQuizData() {
    if (this.#configuration) return this.#configuration;
    if (QuizComponent.#quizData) return QuizComponent.#quizData;
    const url = this.dataset.quizDataUrl;
    if (!url) throw new Error("[quiz] missing data-quiz-data-url attribute");
    // Share the in-flight request too, including an immediate email skip.
    QuizComponent.#quizData = fetch(url).then((response) => {
      if (!response.ok) throw new Error("[quiz] could not load quiz data");
      return response.json();
    }).catch((error) => {
      QuizComponent.#quizData = null;
      throw error;
    });
    return QuizComponent.#quizData;
  }

  #prepareResults() {
    const { energy, intent, wear } = this.answers;
    const key = `${energy}|${intent}|${wear}`;
    // Recheck availability if someone leaves the email step open for a while.
    if (this.#preparedResults?.key === key && Date.now() - this.#preparedResults.startedAt < 60_000) {
      return this.#preparedResults.promise;
    }

    const promise = this.#loadQuizData().then((data) => {
      const requests = quizCandidateHandles(data.combos, energy, intent, wear, data.questions?.find(q => q.id === "wear")?.options.filter(o => o.id !== "all").map(o => o.id))
        .map((handle) => this.#fetchProduct(handle).then((product) =>
          product?.featured_image && typeof product.price === "number" ? product : null
        ));
      // Requests run in parallel, but inspect them in curated order so a fast
      // lower-ranked response never displaces a better match.
      const featured = (async () => {
        const picks = [];
        for (const request of requests) {
          const product = await request;
          if (product) {
            picks.push(product);
            // Start the hero image download before the cards are inserted.
            const image = new Image();
            image.src = product.featured_image;
          }
          if (picks.length === 3) break;
        }
        return picks;
      })();
      return {
        oneLiner: data.one_liners[`${energy}|${intent}`] ?? "",
        featured,
        all: Promise.all(requests).then((products) => products.filter(Boolean)),
      };
    }).catch((error) => {
      if (this.#preparedResults?.promise === promise) this.#preparedResults = null;
      throw error;
    });
    this.#preparedResults = { key, promise, startedAt: Date.now() };
    return promise;
  }

  /**
   * Fetches everything a result card needs in one request.
   *
   * Uses templates/product.quiz-json.liquid rather than Shopify's native
   * /products/{handle}.js, because .js does not expose metafields and the
   * quiz needs two of them (short_quiz_name, reach_for_this_when).
   *
   * @param {string} handle
   */
  async #fetchProduct(handle) {
    // One retry, because a dropped request here is invisible: the visitor just
    // sees fewer matches, and the Klaviyo event records an incomplete list
    // permanently. A single transient failure should not cost a product.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(`/products/${handle}?view=quiz-json`);
        if (!response.ok) {
          if (response.status >= 400 && response.status < 500) return null;
          continue;
        }
        const product = await response.json();
        if (!product.available) return null;

        // The blurb is the one field that arrives HTML-escaped; everything
        // else comes through Liquid's `json` filter, which leaves text alone.
        product.blurb = decodeHtmlEntities(product.blurb);
        const overrides = this.#configuration?.product_overrides?.[handle];
        if (overrides?.short_name?.trim()) product.short_name = overrides.short_name;
        if (overrides?.blurb?.trim()) product.blurb = overrides.blurb;
        return product;
      } catch {
        // Fall through to the retry.
      }
    }

    console.warn(`[quiz] could not load product "${handle}" — it is missing from this result set`);
    return null;
  }

  /**
   * Short display name for the results headline and featured cards.
   *
   * Prefers the curated `custom.short_quiz_name` metafield. When that is
   * empty (it is unpopulated across the catalogue today) it falls back to
   * trimming the catalogue title down to its distinctive part, since the
   * full titles are far too long to read as a headline — e.g.
   *   "Black Spinel for Protection with Boundaries - Crystal Beaded Necklace"
   *     -> "Black Spinel"
   *   "Grounded | Crystal Beaded Charm Necklace"  -> "Grounded"
   *   "Protection Bundle: Evil Eye Charm + ..."   -> "Protection Bundle"
   *
   * @param {any} product
   */
  #shortName(product) {
    const explicit = String(product.short_name ?? "").trim();
    if (explicit) return explicit;

    const title = String(product.title ?? "").trim();
    const separators = [" for ", " | ", ": ", " - ", " + ", "|", ":"];
    let cut = title.length;

    for (const separator of separators) {
      const index = title.indexOf(separator);
      if (index > 0 && index < cut) cut = index;
    }

    return title.slice(0, cut).trim() || title;
  }

  /**
   * First sentence of the `reach_for_this_when` metafield, per spec §5.4.
   * Returns "" when the metafield is empty so the card can omit the line
   * entirely rather than reserving blank space.
   *
   * @param {string} text
   */
  #firstSentence(text) {
    const trimmed = String(text ?? "").trim();
    if (!trimmed) return "";
    const match = trimmed.match(/^.*?[.!?](\s|$)/);
    return (match ? match[0] : trimmed).trim();
  }

  async renderResults() {
    const version = ++this.#renderVersion;
    const answersKey = JSON.stringify(this.answers);
    const isCurrent = () => version === this.#renderVersion &&
      this.dataset.step === "results" && JSON.stringify(this.answers) === answersKey;
    this.dataset.resultsState = "loading";
    this.refs.resultsHeadline.textContent = this.#wording("loading", "Finding your matches…");
    this.refs.resultsOneLiner.textContent = "";
    this.refs.resultsFeatured.replaceChildren();
    this.refs.resultsGrid.replaceChildren();
    this.refs.resultsGridSection.classList.add("hidden");
    this.refs.resultsEmpty.classList.add("hidden");
    try {
      const prepared = await this.#prepareResults();
      const featured = await prepared.featured;
      if (!isCurrent()) return [];
      this.refs.resultsOneLiner.textContent = prepared.oneLiner;

      // Only the hero cards carry a percentage, so the run is sized to them —
      // spreading it across every match would leave all three heroes on the same
      // figure once there were more than a handful. Deliberately a parallel array
      // rather than a field on the products: the list handed back for the
      // Klaviyo event must not carry these numbers.
      const scores = this.#matchScores(featured.length);

      this.refs.resultsHeadline.textContent = this.#wording("results_title", featured.map((product) => this.#shortName(product)).join(", ") || this.#wording("results_fallback_title", "Your matches"));
      this.refs.resultsFeatured.innerHTML = featured
        .map((product, index) => this.#renderFeaturedCard(product, scores[index]))
        .join("");
      this.dataset.resultsState = featured.length ? "ready" : "empty";
      this.refs.resultsEmpty.classList.toggle("hidden", featured.length > 0);

      // The lower grid and email event still receive every match, but no longer
      // hold the first three cards hostage to the slowest product request.
      const available = await prepared.all;
      if (!isCurrent()) return [];
      const rest = available.slice(3);
      // Prefer the theme's own product card so the matches match the rest of
      // the store; `#renderCard` stays as the fallback for a product whose card
      // failed to render server-side.
      const cards = document.createDocumentFragment();
      rest.forEach((product) => {
        const parsed = document.createElement("template");
        parsed.innerHTML = product.card_html || this.#renderCard(product);
        this.#stripBadges(parsed.content);
        for (const link of parsed.content.querySelectorAll('a[href*="/products/"]')) {
          link.setAttribute("href", quizRecommendationUrl(link.getAttribute("href")));
        }
        cards.append(parsed.content);
      });
      this.refs.resultsGrid.replaceChildren(cards);

      // With 3 or fewer matches there is nothing left for the grid — hide its
      // heading and "view all" link rather than leaving them stranded above
      // empty space.
      this.refs.resultsGridSection.classList.toggle("hidden", rest.length === 0);

      // Handed back so submitEmail can put the full match list into Klaviyo.
      return available;
    } catch (error) {
      if (isCurrent()) {
        this.dataset.resultsState = "empty";
        this.refs.resultsHeadline.textContent = this.#wording("results_fallback_title", "Your matches");
        this.refs.resultsEmpty.classList.remove("hidden");
      }
      console.warn("[quiz] could not load results", error);
      return [];
    }
  }

  /**
   * The "% MATCH" figures shown on the hero cards, highest first.
   *
   * These are generated, not measured: the quiz data carries no fit score, and
   * the brief is a plausible spread across the 99–95 range the design uses. The
   * only hard requirement is that the run never climbs as the visitor reads
   * down the page, so each value is clamped to the one above it — past five
   * cards the range runs out and neighbours tie, which is the only honest
   * option inside a five-point range.
   *
   * @param {number} count
   * @returns {number[]}
   */
  #matchScores(count) {
    const top = 99;
    const floor = 95;
    const scores = [];
    let previous = top;

    for (let index = 0; index < count; index += 1) {
      // Spread the run evenly over 99–95, then jitter by a point so the gaps
      // are not identical all the way down.
      const spread = count > 1 ? (index * (top - floor)) / (count - 1) : 0;
      const jittered = Math.round(top - spread) + (Math.random() < 0.5 ? 0 : 1);
      const score = Math.min(previous, Math.max(floor, Math.min(top, jittered)));
      scores.push(score);
      previous = score;
    }

    return scores;
  }

  /**
   * Deliberately not given the theme's `badge` class: the two are both
   * single-class selectors and set the same properties (background, padding,
   * radius, type), so whichever sheet loaded last would win. This pill
   * defines everything it needs on its own.
   *
   * @param {number} score
   */
  #renderMatchBadge(score) {
    return `<span class="quiz-match-badge">${score}% ${escapeHtml(this.#wording("match_label", "Match"))}</span>`;
  }

  /**
   * Clears the badge corner on a "Shop your Matches" card.
   *
   * These cards carry no badge at all — not the match percentage (that belongs
   * to the three hero cards above them) and not the store's own "Protection" /
   * Sale / Sold out pills either. The whole `<product-badge>` element goes
   * rather than just its contents: it is a custom element that re-renders
   * itself from a variant-update event, so emptying it would only invite the
   * badges back.
   *
   * @param {DocumentFragment} card
   */
  #stripBadges(card) {
    for (const badges of card.querySelectorAll("product-badge, .product-card__badges")) {
      badges.remove();
    }
  }

  /**
   * Hero card for the top 3 picks: short name + one-line blurb, no price,
   * matching the Figma results design.
   *
   * @param {any} product
   * @param {number} score
   */
  #renderFeaturedCard(product, score) {
    const blurb = this.#firstSentence(product.blurb);
    return `
      <a class="quiz-featured-card" href="${escapeAttribute(quizRecommendationUrl(product.url))}">
        <span class="quiz-featured-card__media">
          <img class="quiz-featured-card__image" src="${escapeAttribute(product.featured_image)}" width="207" height="200" alt="${escapeAttribute(product.title)}" loading="eager">
          ${this.#renderMatchBadge(score)}
        </span>
        <span class="quiz-featured-card__body">
          <span class="quiz-featured-card__name">${escapeHtml(this.#shortName(product))}</span>
          ${blurb ? `<span class="quiz-featured-card__blurb">${escapeHtml(blurb)}</span>` : ""}
        </span>
      </a>
    `;
  }

  /**
   * Grid card for the "Shop your Matches" section.
   *
   * A div rather than an anchor: the design puts an add-to-cart button on the
   * card, and a button nested inside a link is invalid HTML that browsers
   * reflow out of the anchor. The link covers the card through a stretched
   * ::after instead, with the button lifted above it.
   *
   * The category slot and the stars each render only when the product
   * actually carries that data, the same way the featured card's blurb does.
   *
   * @param {any} product
   */
  #renderCard(product) {
    const category = String(product.category ?? "").trim();
    const rating = Number(product.rating);
    const stars = Number.isFinite(rating) && rating > 0 ? this.#renderStars(product) : "";
    const cartIcon = this.#icon("cart");

    return `
      <div class="quiz-result-card">
        <a class="quiz-result-card__link" href="${escapeAttribute(quizRecommendationUrl(product.url))}">
          <img class="quiz-result-card__image" src="${escapeAttribute(product.featured_image)}" width="346" height="356" alt="${escapeAttribute(product.title)}" loading="lazy">
          <span class="quiz-result-card__body">
            ${category ? `<span class="quiz-result-card__category">${escapeHtml(category)}</span>` : ""}
            <span class="quiz-result-card__title">${escapeHtml(this.#shortName(product))}</span>
            ${stars}
            <span class="quiz-result-card__price">${formatCurrency(product.price)}</span>
          </span>
        </a>
        ${
          product.variant_id && cartIcon
            ? `<button type="button" class="quiz-result-card__add" data-variant-id="${escapeAttribute(product.variant_id)}" aria-label="Add ${escapeAttribute(product.title)} to cart" on:click="quiz-component/addToCart">${cartIcon}</button>`
            : ""
        }
      </div>
    `;
  }

  /**
   * Clones one of the theme's own icons out of the templates in
   * sections/quiz.liquid. Returns "" when the template is missing so the
   * caller can drop the control rather than render an empty box.
   *
   * @param {string} name
   */
  #icon(name) {
    const template = this.querySelector(`.quiz__icon-source[data-icon="${name}"]`);
    return template instanceof HTMLTemplateElement ? template.innerHTML.trim() : "";
  }

  /**
   * Review stars, from the `reviews.rating` metafield the theme's own
   * product-review-stars snippet reads. Each star carries a clipped copy of
   * itself so a 4.9 shows as four full stars and a nine-tenths one.
   *
   * @param {any} product
   */
  #renderStars(product) {
    const star = this.#icon("star");
    if (!star) return "";

    const rating = Number(product.rating);
    const max = Number(product.rating_max) || 5;
    const count = Number(product.rating_count) || 0;

    let markup = "";
    for (let index = 1; index <= max; index += 1) {
      const fill = Math.max(0, Math.min(1, rating - (index - 1)));
      markup += `<span class="quiz-stars__star">${star}<span class="quiz-stars__star-fill" style="width: ${(fill * 100).toFixed(1)}%">${star}</span></span>`;
    }

    const label = count
      ? `Rated ${rating} out of ${max} from ${count} reviews`
      : `Rated ${rating} out of ${max}`;
    return `<span class="quiz-stars" role="img" aria-label="${escapeAttribute(label)}">${markup}</span>`;
  }

  /**
   * Adds a match to the cart without leaving the results.
   *
   * Mirrors assets/charm-builder.js: the same broadcast the theme's product
   * form uses collects the section ids that care about cart updates, so their
   * HTML comes back pre-rendered in the /cart/add.js response and the drawer
   * opens itself rather than needing a follow-up fetch.
   *
   * @param {Event} event
   */
  async addToCart(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest(".quiz-result-card__add") : null;
    if (!button || button.hasAttribute("aria-disabled")) return;

    // The card's stretched link sits under the button; without this a click
    // both adds to cart and navigates to the product page.
    event.preventDefault();

    const variantId = Number(button.dataset.variantId);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    button.setAttribute("aria-disabled", "true");
    button.classList.add("is-loading");

    try {
      const properties = await prepareExperienceAdd("quiz");
      const sections = [];
      document.dispatchEvent(new CartGroupedSections(sections));

      const response = await fetch(
        window.FoxTheme.routes.cart_add_url,
        fetchConfig("json", {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }],
            sections: [...new Set(sections)].join(","),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      if (!response.ok || data.status) {
        console.error("[quiz] add to cart rejected", data.description || data.message);
        return;
      }

      void trackExperience("quiz_added_to_cart");
      this.dispatchEvent(
        new CartUpdateEvent(data, "quiz", {
          source: "quiz",
          itemCount: this.#parseItemCountFromSections(data.sections),
          sections: data.sections,
        })
      );
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: data } }));
    } catch (error) {
      console.error("[quiz] add to cart failed", error);
    } finally {
      button.removeAttribute("aria-disabled");
      button.classList.remove("is-loading");
    }
  }

  /**
   * Reads the cart's true item count out of the section HTML the add response
   * carries, the same two-tier lookup assets/cart-items.js uses. Returns
   * undefined when no section carried it, which the drawer already guards for.
   *
   * @param {Record<string, string> | undefined} sections
   */
  #parseItemCountFromSections(sections) {
    if (!sections) return undefined;
    for (const html of Object.values(sections)) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const countEl = doc.querySelector('[ref="cartItemCount"]') ?? doc.querySelector(".cart-bubble__text-count");
      const count = countEl?.textContent ? parseInt(countEl.textContent, 10) : NaN;
      if (!Number.isNaN(count)) return count;
    }
    return undefined;
  }
}

if (!customElements.get("quiz-component")) {
  customElements.define("quiz-component", QuizComponent);
}
